/**
 * React binding for createInvitationActions. OrgDetailPage calls it once, for the organization it
 * shows, and passes the result to the invitations panel and the invite dialog as their `actions`
 * prop: the Users subtree mounts only while its tab is active, so a controller owned
 * by the panel would be disposed by every tab switch and replaced by an empty one, dropping holds,
 * reconcile failures, send keys and cooldowns while the page still shows the same rows.
 * One controller per orgId: created in a layout effect once orgId is set and disposed in that
 * effect's cleanup (a layout effect, so the old org's controller never serves a painted frame
 * after an org switch), so an orgId change, the page's unmount and StrictMode's simulated unmount
 * (the app renders under React.StrictMode) each end the old controller's continuations, so
 * nothing settles after dispose, and the next setup starts a fresh, empty one; a controller memoised across that
 * cleanup would stay disposed. A tab switch or a dialog close runs no cleanup here. Its state is
 * mirrored into React state. The callbacks are read through a ref so the caller may pass fresh
 * closures every render (and `currentAcceptUrl` sees the latest list), and each receives the
 * controller's own orgId, so a continuation that settles after the route moved on is dropped by
 * the page's org-scoped reader and applyInvitationUpdate.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { adminApi, type AdminInvitation } from '../services/adminApi';
import {
  INITIAL_INVITATION_ACTIONS_STATE,
  createInvitationActions,
  type InvitationActions,
  type InvitationActionsState,
} from './invitationActions';

/** The page's side of the controller's dependencies, each told which organization's controller asks. */
export interface InvitationActionsCallbacks {
  /** Awaited after every send failure and every revoke; see InvitationActionsDeps.onReconcile. */
  onReconcile: (orgId: string) => Promise<boolean>;
  /** The DTO a successful send returned, for the org whose controller sent it. */
  onSent: (orgId: string, invitation: AdminInvitation) => void;
  /** The invitation's current usable link in the page's list, or null (also for another org). */
  currentAcceptUrl: (orgId: string, invitationId: string) => string | null;
  /** The invitation's current row in the page's list, or undefined (also for another org). */
  currentInvitation: (orgId: string, invitationId: string) => AdminInvitation | undefined;
}

/** What the page passes to the panel and the invite dialog as their `actions` prop. */
export interface UseInvitationActions extends InvitationActionsState {
  send: InvitationActions['send'];
  copyLink: InvitationActions['copyLink'];
  revoke: InvitationActions['revoke'];
  retryReconcile: InvitationActions['retryReconcile'];
}

export function useInvitationActions(
  orgId: string | undefined,
  callbacks: InvitationActionsCallbacks,
): UseInvitationActions {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const controllerRef = useRef<InvitationActions | null>(null);
  const [state, setState] = useState<InvitationActionsState>(INITIAL_INVITATION_ACTIONS_STATE);

  useLayoutEffect(() => {
    if (!orgId) {
      controllerRef.current = null;
      setState(INITIAL_INVITATION_ACTIONS_STATE);
      return undefined;
    }
    const controller = createInvitationActions({
      sendInvitation: (invitationId, requestId) => adminApi.sendInvitation(orgId, invitationId, requestId),
      revokeInvitation: (invitationId) => adminApi.revokeInvitation(orgId, invitationId),
      confirm: (message) => window.confirm(message),
      writeClipboard: (text) => navigator.clipboard.writeText(text),
      now: () => new Date(),
      newRequestId: () => crypto.randomUUID(),
      currentAcceptUrl: (invitationId) => callbacksRef.current.currentAcceptUrl(orgId, invitationId),
      currentInvitation: (invitationId) => callbacksRef.current.currentInvitation(orgId, invitationId),
      onSent: (invitation) => callbacksRef.current.onSent(orgId, invitation),
      onReconcile: () => callbacksRef.current.onReconcile(orgId),
      onChange: setState,
    });
    controllerRef.current = controller;
    setState(controller.getState());
    return () => controller.dispose();
  }, [orgId]);

  // Stable wrappers over the current controller; before the first effect has run, while orgId is
  // unset, or once it is disposed, every action is refused, as on a disposed controller.
  const send = useCallback<InvitationActions['send']>(
    (inv) => controllerRef.current?.send(inv) ?? Promise.resolve(null),
    [],
  );
  const copyLink = useCallback<InvitationActions['copyLink']>(
    (inv) => controllerRef.current?.copyLink(inv) ?? Promise.resolve(),
    [],
  );
  const revoke = useCallback<InvitationActions['revoke']>(
    (inv) => controllerRef.current?.revoke(inv) ?? Promise.resolve(false),
    [],
  );
  const retryReconcile = useCallback<InvitationActions['retryReconcile']>(
    (invitationId) => controllerRef.current?.retryReconcile(invitationId) ?? Promise.resolve(false),
    [],
  );

  return { ...state, send, copyLink, revoke, retryReconcile };
}
